import React, {ChangeEvent, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    HStack,
    Image,
    Input,
    VStack,
} from "@chakra-ui/react";
import {LoginRequestI} from "@features/auth/types";
import {loginWithPasswordSchema} from "@pages/auth/schemas/login-with-password-schema";
import {defaultLoginSchema} from "@pages/auth/schemas/default-login-schema";
import LanguageDropdown from "@components/language-dropdown";
import LocalizedLink from "@components/localized-link";
import logo from "@assets/images/logo.svg";
import {websiteSettingsResI} from "@features/website-settings/types";
import {useAuth} from "@hooks/useAuth";

interface LoginFormProps {
    websiteSettings: websiteSettingsResI;
    lang: string;
}

const LoginForm: React.FC<LoginFormProps> = ({websiteSettings, lang}) => {
    const [membershipValue, setMembershipValue] = useState("");
    const {t} = useTranslation();

    const { login } = useAuth();

    const {
        control,
        handleSubmit,
        reset,
        getValues,
        formState: {errors},
    } = useForm<LoginRequestI>({
        mode: "all",
        resolver: yupResolver(websiteSettings?.loginWithPassword ? loginWithPasswordSchema(t) : defaultLoginSchema(t)),
        defaultValues: {
            userLogonId: "",
            userName: "",
            userFirstName: "",
        },
    });

    const handleMembershipChange = (e: ChangeEvent<HTMLInputElement>) => {
        const regex = /^[a-zA-Z0-9_-]*$/;
        if (e.target.value === "" || regex.test(e.target.value)) {
            setMembershipValue(e.target.value);
            reset({userLogonId: e.target.value});
        }
    };

    // This is only for development reasons
    const addQuickLoginData = () => {
        setMembershipValue("1000");
        reset({
            userLogonId: "1000",
            userFirstName: "(Sjoerd)",
            userName: "Test",
        });
    };

    // This is only for development reasons
    const addQuickLoginDataWithPassword = () => {
        setMembershipValue("1000");
        reset({
            userLogonId: "1000",
            userName: "Sjoerd",
            userFirstName: "",
        });
    };

    const logonID = getValues("userLogonId");

    // @ts-ignore
    return (
        <VStack align={"flex-start"} spacing={"40px"}>
            <Box position={"absolute"} top={"20px"} right={"20px"} w={"auto"}>
                <LanguageDropdown/>
            </Box>
            <LocalizedLink display={"inline-block"}>
                <Image
                    src={logo}
                    alt={"logo"}
                    maxH={"100px"}
                    w={"full"}
                />
            </LocalizedLink>


            <VStack
                as={"form"}
                onSubmit={handleSubmit((data: LoginRequestI) =>
                    login({
                        ...data,
                        "userLanguage": lang.toUpperCase()
                    })
                )}
                w={"full"}
                noValidate
            >
                <VStack w="full" align={"flex-start"} spacing={"32px"}>
                    <Heading
                        fontSize={{base: "xl", md: "2xl"}}
                        fontWeight={"semibold"}
                        color={"black"}
                    >
                        {t("auth.login.title")}
                    </Heading>
                    <VStack w={"full"} spacing={"20px"}>
                        <FormControl isInvalid={!!errors?.userLogonId} isRequired>
                            <FormLabel htmlFor={"userLogonId"}>
                                {t("auth.login.membershipCardNumber")}
                            </FormLabel>
                            <Controller
                                name={"userLogonId"}
                                control={control}
                                render={({field}) => (
                                    <Input
                                        {...field}
                                        variant={"filled"}
                                        id={"lidkaartnummer"}
                                        placeholder={t("auth.login.membershipCardNumber")}
                                        value={membershipValue}
                                        onChange={handleMembershipChange}
                                    />
                                )}
                            />
                            <FormErrorMessage>
                                {!!errors?.userLogonId && errors?.userLogonId?.message}
                            </FormErrorMessage>
                        </FormControl>
                        {!websiteSettings.loginWithPassword &&
                            <FormControl isInvalid={!logonID.startsWith("TRA") && !!errors?.userFirstName}
                                         isRequired={!logonID.startsWith("TRA")}>
                                <FormLabel htmlFor={"userFirstName"}>
                                    {t("auth.login.firstName")}
                                </FormLabel>
                                <Controller
                                    name={"userFirstName"}
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            {...field}
                                            id={"voornaam"}
                                            variant={"filled"}
                                            placeholder={t("auth.login.firstName")}
                                        />
                                    )}
                                />
                                <FormErrorMessage>
                                    {!logonID.startsWith("TRA") && !!errors?.userFirstName && errors?.userFirstName?.message}
                                </FormErrorMessage>
                            </FormControl>
                        }
                        <FormControl isInvalid={!logonID.startsWith("TRA") && !!errors?.userName}
                                     isRequired={!logonID.startsWith("TRA")}>
                            <FormLabel htmlFor={"userName"}>
                                {websiteSettings.loginWithPassword ? t("auth.login.password") : t("auth.login.lastName")}
                            </FormLabel>
                            <Controller
                                name={"userName"}
                                control={control}
                                render={({field}) => (
                                    <Input
                                        {...field}
                                        id={"naam"}
                                        variant={"filled"}
                                        placeholder={websiteSettings.loginWithPassword ? t("auth.login.password") : t("auth.login.lastName")}
                                    />
                                )}
                            />
                            <FormErrorMessage>
                                {!logonID.startsWith("TRA") && !!errors?.userName && errors?.userName?.message}
                            </FormErrorMessage>
                        </FormControl>
                    </VStack>
                    <HStack spacing={"12px"} w={"full"}>
                        {process.env.NODE_ENV !== "production" && (
                            <Button
                                w={"full"}
                                variant={"secondary"}
                                onClick={() => websiteSettings.loginWithPassword ? addQuickLoginDataWithPassword() : addQuickLoginData()}
                            >
                                Prefill login
                            </Button>
                        )}
                        <Button type={"submit"} w={"full"} variant={"primary"}>
                            {t("auth.login.confirmBtn")}
                        </Button>
                    </HStack>
                </VStack>
            </VStack>
        </VStack>
    );
};

export default LoginForm;
